import { useCallback, useRef, useState } from 'react'

/**
 * Copy to clipboard, with the two seconds of feedback that make it trustworthy.
 *
 * Without the confirmation a rep clicks twice, then pastes somewhere to check.
 * The hook returns *which* key was copied rather than a boolean, so several
 * buttons can share one instance without all of them flashing at once.
 *
 * Falls back to a hidden textarea where `navigator.clipboard` is unavailable —
 * rare, but its failure mode is silent, and silently copying nothing is the
 * worst outcome for a button whose whole job is to have copied something.
 */
export function useCopy(resetAfterMs = 2000): {
  copied: string | null
  copy: (key: string, text: string) => Promise<void>
} {
  const [copied, setCopied] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const copy = useCallback(
    async (key: string, text: string) => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const el = document.createElement('textarea')
          el.value = text
          el.setAttribute('readonly', '')
          el.style.position = 'fixed'
          el.style.opacity = '0'
          document.body.appendChild(el)
          el.select()
          document.execCommand('copy')
          document.body.removeChild(el)
        }
        setCopied(key)
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(() => setCopied(null), resetAfterMs)
      } catch {
        setCopied(null)
      }
    },
    [resetAfterMs],
  )

  return { copied, copy }
}
